import { useState, type KeyboardEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { IconPlus } from '@/components/Icons';
import { NewReceiptModal } from '@/components/receipts/NewReceiptModal';
import { ContributionsPanel } from '@/components/leverage/ContributionsPanel';
import { OperationsPanel } from '@/components/leverage/OperationsPanel';
import '@/components/leverage/leverage.css';

type Tab = 'operations' | 'contributions';

const TABS: { value: Tab; label: string }[] = [
  { value: 'operations', label: 'Novo sinal' },
  { value: 'contributions', label: 'Nova alavancagem' },
];

const TAB_KEY = 'painel.alavancagem.aba';

function readTab(): Tab {
  try {
    return localStorage.getItem(TAB_KEY) === 'contributions' ? 'contributions' : 'operations';
  } catch {
    return 'operations';
  }
}

function saveTab(tab: Tab) {
  try {
    localStorage.setItem(TAB_KEY, tab);
  } catch {
    // Armazenamento bloqueado: a aba vale até recarregar.
  }
}

/**
 * Alavancagem. O membro registra quanto cada cliente aportou; o administrador
 * monta as operações e distribui uma fatia do capital de cada cliente — e
 * também vê e corrige os aportes, na outra aba.
 */
export function LeveragePage() {
  const { isAdmin } = useAuth();
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<Tab>(readTab);

  function choose(next: Tab) {
    setTab(next);
    saveTab(next);
  }

  /** Setas trocam de aba, como pede o padrão de abas. */
  function onTabKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const index = TABS.findIndex((item) => item.value === tab);
    const next = TABS[(index + (event.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length]!.value;
    choose(next);
    document.getElementById(`lev-tab-${next}`)?.focus();
  }

  const newContribution = (
    <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
      <IconPlus /> Nova alavancagem
    </button>
  );

  return (
    <>
      <div className="lev-page-head">
        <div style={{ minWidth: 0 }}>
          <h1 className="page-title">Alavancagem</h1>
          <p className="page-lede">
            {isAdmin
              ? 'Monte as operações e distribua uma fatia do capital de cada cliente. Somando as operações abertas, ninguém passa de 100% do capital.'
              : 'Registre o depósito com alavancagem de cada cliente, com o comprovante: o depositado entra no capital dele. Se a conta na corretora ainda não tem login ou senha, complete no mesmo lugar.'}
          </p>
        </div>
        {!isAdmin || tab === 'contributions' ? <div className="lev-page-action">{newContribution}</div> : null}
      </div>

      {isAdmin ? (
        <>
          <div className="lev-tabs" role="tablist" aria-label="Alavancagem" onKeyDown={onTabKey}>
            {TABS.map((item) => (
              <button
                key={item.value}
                type="button"
                role="tab"
                id={`lev-tab-${item.value}`}
                aria-selected={tab === item.value}
                aria-controls="lev-panel"
                tabIndex={tab === item.value ? 0 : -1}
                className="lev-tab"
                onClick={() => choose(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div id="lev-panel" role="tabpanel" aria-labelledby={`lev-tab-${tab}`}>
            {tab === 'operations' ? <OperationsPanel /> : <ContributionsPanel isAdmin onCreate={() => setCreating(true)} />}
          </div>
        </>
      ) : (
        <ContributionsPanel isAdmin={false} onCreate={() => setCreating(true)} />
      )}

      {/* Um depósito, um lugar só: a nova alavancagem é o comprovante de depósito, que vira o aporte do cliente. */}
      {creating ? <NewReceiptModal defaultType="WELCOME" title="Nova alavancagem" onClose={() => setCreating(false)} /> : null}
    </>
  );
}
